import { join, resolve } from "node:path";
// `avatar add repo` — v3. Cài repo code vào src/<name> bằng git clone.
//
// 3 nguồn: ① repo có sẵn (URL), ② thư mục đã có (clone từ remote của nó),
// ③ dự án mới (tạo repo GitHub rồi clone). Sau clone: ghi repos.json, hỏi
// GitNexus index per-repo + wiki. Loop add tiếp.
import type { Command } from "commander";
import {
  type AddRepoFlags,
  assertAddRepoFlagsComplete,
  type RemoveRepoFlags,
  registerAddRepoFlags,
  registerRemoveRepoFlags,
  requireOneOf,
  resolveDeleteFolderDecision,
  VISIBILITIES,
  type Visibility,
} from "../lib/add-repo-non-interactive-flags.js";
import { assertInAvatarWorkspaceOrExit } from "../lib/assert-in-avatar-workspace-or-hint-foreign.js";
import {
  folderDirtyStatus,
  inferRepoNameFromUrl,
  readFolderRemoteUrl,
  scaffoldNewRepoInSrc,
} from "../lib/clone-code-repo-into-src.js";
import { cloneRepoWithRecovery } from "../lib/clone-repo-with-recovery-menu.js";
import {
  createEmptyRepoWithRecovery,
  createRemoteFromFolderWithRecovery,
} from "../lib/create-github-remote-with-recovery-menu.js";
import { detectGitnexusInstallation } from "../lib/detect-gitnexus-installation.js";
import { ensureGitHubReady } from "../lib/git-auth-and-install-orchestrator.js";
import { isGithubHost, parseGitRemoteHost } from "../lib/git-remote-host.js";
import { buildAvatarRepoGitignoreBlock } from "../lib/gitignore-template-loader.js";
import { isNonInteractiveFatalError } from "../lib/non-interactive-abort-error.js";
import { isNonInteractive } from "../lib/non-interactive-context.js";
import { confirmOrFlag, inputOrFlag, selectOrFlag } from "../lib/prompt-or-flag.js";
import { runGitnexusAnalyze } from "../lib/run-gitnexus-setup-and-analyze.js";
import { runGitnexusWikiConditional } from "../lib/run-gitnexus-wiki-conditional.js";
import { log } from "../lib/terminal-logger.js";
import { writeAvatarBlockToGitExclude } from "../lib/write-avatar-block-to-git-exclude.js";

const ADD = "add repo";
const ADD_MORE_FLAG_SPEC = {
  flag: "--yes",
  command: ADD,
  hint: "--yes (chỉ add 1 repo mỗi lần gọi)",
};
const DELETE_FILES_FLAG_SPEC = {
  flag: "--keep-files|--delete-files",
  command: "remove repo",
  hint: "--keep-files (giữ folder) hoặc --delete-files (xoá folder)",
};
const SOURCE_FLAG_SPEC = {
  flag: "--repo-source",
  command: ADD,
  hint: "--repo-source url|folder|new",
};
const URL_FLAG_SPEC = { flag: "--url", command: ADD, hint: "--url git@github.com:org/repo.git" };
const FOLDER_FLAG_SPEC = {
  flag: "--folder-path",
  command: ADD,
  hint: "--folder-path /path/toi/folder",
};
const DIRTY_FLAG_SPEC = {
  flag: "--on-dirty-folder",
  command: ADD,
  hint: "--on-dirty-folder abort|proceed",
};
const NEW_NAME_FLAG_SPEC = {
  flag: "--new-repo-name",
  command: ADD,
  hint: "--new-repo-name ten-repo",
};
const VISIBILITY_FLAG_SPEC = {
  flag: "--remote-visibility",
  command: ADD,
  hint: "--remote-visibility private|public",
};
const CREATE_REMOTE_FLAG_SPEC = {
  flag: "--create-remote",
  command: ADD,
  hint: "--create-remote yes|no",
};
const NAME_FLAG_SPEC = { flag: "--name", command: ADD, hint: "--name ten-thu-muc" };

export function registerAddCommand(program: Command): void {
  const add = program.command("add").description("Thêm tài nguyên vào workspace");
  const addRepo = add
    .command("repo")
    .description("Clone 1 repo code vào src/ (repo có sẵn / thư mục / dự án mới)")
    .option("--url <url>", "URL git repo có sẵn (bỏ qua prompt nguồn)")
    .option("--name <name>", "Tên thư mục trong src/ (mặc định suy từ URL)")
    .option("--yes", "Auto-confirm prompt");
  registerAddRepoFlags(addRepo);
  addRepo.action(async (opts: AddRepoFlags) => {
    try {
      await runAddRepo(opts);
    } catch (err) {
      if (isNonInteractiveFatalError(err)) throw err;
      log.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  });

  // avatar remove repo <name> — gỡ repo khỏi workspace (manifest + hỏi xóa folder).
  const remove = program.command("remove").description("Gỡ tài nguyên khỏi workspace");
  const removeRepo = remove
    .command("repo <name>")
    .description("Gỡ repo khỏi src/ (xóa khỏi repos.json + tùy chọn xóa folder)")
    .option("--keep-files", "Chỉ gỡ khỏi manifest, GIỮ folder src/<name>");
  registerRemoveRepoFlags(removeRepo);
  removeRepo.action(async (name: string, opts: RemoveRepoFlags) => {
    try {
      await runRemoveRepo(name, opts);
    } catch (err) {
      if (isNonInteractiveFatalError(err)) throw err;
      log.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  });

  // avatar list repo — liệt kê repo trong workspace.
  const list = program.command("list").description("Liệt kê tài nguyên trong workspace");
  list
    .command("repo")
    .description("Liệt kê repo trong src/")
    .action(async () => {
      try {
        await runListRepo();
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

// Validate tên repo GitHub: chỉ alphanumeric + dấu . - _ (GitHub rules).
// Không space, không '/', không rỗng.
// Export để unit-test (guard path-traversal N2). Dùng nội bộ làm inquirer `validate`.
export function validateGithubRepoName(v: string): true | string {
  const trimmed = v.trim();
  if (trimmed.length === 0) return "Tên bắt buộc";
  if (!/^[A-Za-z0-9._-]+$/.test(trimmed)) {
    return "Tên repo chỉ gồm chữ/số/dấu . - _ (không space, không '/').";
  }
  // Chặn tên toàn dấu chấm ('.', '..', ...): regex trên đã loại '/', nhưng '..'
  // vẫn lọt → join(root, "src", "..") = workspace root (path traversal). finalizeAddedRepo
  // & patchAllReposGitExclude dùng thẳng path này để ghi <git-common-dir>/info/exclude.
  if (/^\.+$/.test(trimmed)) {
    return "Tên repo không được toàn dấu chấm ('.', '..') — tránh ghi ra ngoài src/.";
  }
  return true;
}

function ensureWorkspace(): string {
  return assertInAvatarWorkspaceOrExit(process.cwd());
}

async function runAddRepo(opts: AddRepoFlags): Promise<void> {
  const workspaceRoot = ensureWorkspace();

  // Non-interactive: kiểm đủ cờ theo nguồn TRƯỚC khi chạm mạng/đĩa.
  const ni = isNonInteractive();
  if (ni) assertAddRepoFlagsComplete(opts);

  let addMore = true;
  let firstUrl = opts.url;
  let firstName = opts.name;

  while (addMore) {
    const source = await resolveRepoSource(firstUrl, ni ? opts : undefined);

    // Bug #68: GitHub auth CHỈ khi thật sự cần — không ép mọi nguồn repo login
    // GitHub như trước. Mode clone + URL github.com → ensure gh cài + login
    // (clone private repo cần token). Host khác (GitLab/Bitbucket/self-hosted)
    // → bỏ qua gh; nếu remote private, cloneRepoWithRecovery đã có menu nhập
    // PAT theo host. Nhánh new/folder-tạo-remote tự gọi ensureGitHubReady()
    // bên trong (chắc chắn cần GitHub).
    if (cloneSourceNeedsGithubAuth(source)) {
      await ensureGitHubReady();
    }

    let repoPath: string | null = null;
    let repoName: string | null = null;

    if (source.mode === "new") {
      // Dự án mới: tạo repo local trong src/<name> + git init + remote + push.
      // KHÔNG clone (repo vừa tạo rỗng, clone về vô nghĩa + dễ fail SSH/timing).
      const name = firstName ?? (await promptRepoName(source.url, ni));
      const created = await scaffoldNewRepoInSrc({ workspaceRoot, url: source.url, name });
      repoPath = created.path;
      repoName = created.name;
      log.success(`✓ Tạo src/${created.name} (repo mới)`);
    } else {
      // Clone (repo có sẵn / folder) với recovery menu.
      const name = firstName ?? (await promptRepoName(source.url, ni));
      log.info(`Clone ${source.url} → src/${name} ...`);
      const { cloned, skipped } = await cloneRepoWithRecovery({
        workspaceRoot,
        url: source.url,
        name,
        nonInteractiveFlags: ni ? opts : undefined,
      });
      if (skipped || !cloned) {
        log.dim(`Bỏ qua repo ${name}.`);
      } else {
        repoPath = cloned.path;
        repoName = cloned.name;
        log.success(`✓ Đã clone src/${cloned.name}`);
      }
    }

    // Điểm hội tụ DUY NHẤT của cả 2 nhánh (new scaffold + clone). MỌI repo add vào src/ PHẢI đi
    // qua finalizeAddedRepo (ghi Avatar block vào <git-common-dir>/info/exclude + index). Nếu
    // refactor tách 1 nhánh ra khỏi đây → repo đó mất Avatar exclude mà test seam KHÔNG bắt.
    if (repoPath && repoName) {
      await finalizeAddedRepo(workspaceRoot, repoPath, repoName);
    }

    // Reset cho vòng sau + hỏi add tiếp.
    firstUrl = undefined;
    firstName = undefined;
    // Non-interactive: một lần gọi = một repo. Hỏi "add tiếp?" khi không ai trả
    // lời sẽ treo; mà tự trả lời "có" thì lặp vô hạn.
    if (opts.yes || ni) break;
    addMore = await confirmOrFlag(undefined, ADD_MORE_FLAG_SPEC, {
      message: "Add repo khác nữa?",
      default: false,
    });
  }
}

// avatar remove repo <name> — gỡ repo khỏi manifest + tùy chọn xóa folder.
async function runRemoveRepo(name: string, opts: RemoveRepoFlags): Promise<void> {
  const workspaceRoot = ensureWorkspace();

  // SECURITY: validate tên TRƯỚC mọi thao tác fs. Chặn path-traversal
  // (`../../x`) khiến removeRecursive xóa thư mục NGOÀI workspace/src.
  const { validateRepoName } = await import("../lib/clone-code-repo-into-src.js");
  const nameError = validateRepoName(name);
  if (nameError) {
    log.error(`Tên repo không hợp lệ: ${nameError}`);
    process.exit(1);
  }

  const { readReposManifest, removeRepoFromManifest } = await import(
    "../lib/workspace-repos-manifest-store.js"
  );
  const { removeRecursive } = await import("../lib/filesystem-helpers.js");
  const { join, relative, sep } = await import("node:path");

  const repos = await readReposManifest(workspaceRoot);
  const entry = repos.find((r) => r.name === name);
  const folderPath = join(workspaceRoot, "src", name);

  // Defense-in-depth: assert folderPath thực sự nằm trong <workspace>/src.
  const srcRoot = join(workspaceRoot, "src");
  const rel = relative(srcRoot, folderPath);
  if (rel === "" || rel === ".." || rel.startsWith(`..${sep}`) || rel.startsWith("/")) {
    log.error(`Đường dẫn repo escape khỏi workspace/src — từ chối xóa: ${folderPath}`);
    process.exit(1);
  }
  const { pathExists } = await import("../lib/filesystem-helpers.js");
  const folderExists = await pathExists(folderPath);

  if (!entry && !folderExists) {
    log.warn(`Repo "${name}" không có trong workspace (cả manifest lẫn src/).`);
    return;
  }

  // ⛔ LỖ L8 — VỊ TRÍ CỦA KHỐI NÀY LÀ MỘT PHẦN CỦA HỢP ĐỒNG.
  // Phải đứng SAU khi `folderExists` đã tính, và TRƯỚC `removeRepoFromManifest`:
  //   · đặt sau `removeRepoFromManifest` ⇒ ca thiếu cờ để lại workspace nửa vời
  //     (mất trong repos.json mà folder vẫn còn);
  //   · đặt vô điều kiện ở đầu hàm ⇒ hồi quy 2 đường đang chạy sạch không prompt:
  //     (a) folder đã bị xoá tay (`folderExists === false`), (b) không có cả entry
  //     lẫn folder (đã return ở trên).
  // Điều kiện dưới đây khớp ĐÚNG điều kiện của confirm xoá folder bên dưới.
  const deleteFolder =
    folderExists && isNonInteractive() ? resolveDeleteFolderDecision(opts) : undefined;

  // Gỡ khỏi manifest.
  await removeRepoFromManifest(workspaceRoot, name);
  log.success(`✓ Gỡ "${name}" khỏi repos.json`);

  // Xóa folder (trừ khi --keep-files). Hỏi xác nhận vì xóa code = mất data.
  if (folderExists && !opts.keepFiles) {
    log.warn(`⚠ Folder src/${name} chứa code. Xóa = mất data nếu chưa push remote.`);
    const confirmDelete = await confirmOrFlag(deleteFolder, DELETE_FILES_FLAG_SPEC, {
      message: `Xóa luôn folder src/${name}?`,
      default: false,
    });
    if (confirmDelete) {
      await removeRecursive(folderPath);
      log.success(`✓ Đã xóa src/${name}`);
    } else {
      log.dim(`Giữ folder src/${name}. (Đã gỡ khỏi manifest — chạy 'avatar add repo' để add lại.)`);
    }
  } else if (folderExists && opts.keepFiles) {
    log.dim(`Giữ folder src/${name} (--keep-files).`);
  }
}

// avatar list repo — liệt kê repo trong workspace + cảnh báo lệch manifest/folder.
async function runListRepo(): Promise<void> {
  const workspaceRoot = ensureWorkspace();
  const { readReposManifest } = await import("../lib/workspace-repos-manifest-store.js");
  const { pathExists } = await import("../lib/filesystem-helpers.js");
  const { join } = await import("node:path");

  const repos = await readReposManifest(workspaceRoot);
  if (repos.length === 0) {
    log.dim("Chưa có repo nào. Thêm bằng 'avatar add repo'.");
    return;
  }

  log.info(`Repo trong workspace (${repos.length}):`);
  for (const r of repos) {
    const exists = await pathExists(join(workspaceRoot, "src", r.name));
    const mark = exists ? "✓" : "⚠ folder thiếu";
    log.plain(`  ${mark} ${r.name.padEnd(28)} ${r.url}`);
  }
}

export interface RepoSource {
  url: string; // remote URL (clone-from hoặc add-remote-to-new)
  mode: "clone" | "new"; // clone: repo có sẵn → git clone. new: repo rỗng → tạo local.
}

// Bug #68: gate quyết định nguồn CLONE có cần GitHub auth không (pure, testable).
// true CHỈ khi clone từ github.com — GitLab/Bitbucket/self-hosted trả false
// (credential lo bởi PAT recovery menu trong cloneRepoWithRecovery).
// Mode "new" trả false ở đây vì nhánh new tự gọi ensureGitHubReady() bên trong
// resolveRepoSource (trước khi tạo repo GitHub).
export function cloneSourceNeedsGithubAuth(source: RepoSource): boolean {
  return source.mode === "clone" && isGithubHost(parseGitRemoteHost(source.url));
}

// Resolve nguồn repo: trả { url, mode }. --url → clone. Else hỏi 3 nguồn.
async function resolveRepoSource(presetUrl?: string, flags?: AddRepoFlags): Promise<RepoSource> {
  if (presetUrl) return { url: presetUrl, mode: "clone" };

  const source = await selectOrFlag(
    flags ? resolveSourceFromFlags(flags) : undefined,
    SOURCE_FLAG_SPEC,
    {
      message: "Nguồn repo?",
      choices: [
        { name: "1. Repo có sẵn (điền URL git)", value: "url" as const },
        { name: "2. Thư mục đã có trên máy (clone từ remote của nó)", value: "folder" as const },
        { name: "3. Dự án mới hoàn toàn (tạo repo GitHub)", value: "new" as const },
      ],
    },
  );

  if (source === "url") {
    const url = await inputOrFlag(flags?.url, URL_FLAG_SPEC, {
      message: "URL git repo:",
      validate: (v) => (v.trim().length > 0 ? true : "URL bắt buộc"),
    });
    return { url, mode: "clone" };
  }

  if (source === "folder") {
    const { statSync } = await import("node:fs");
    const folderPath = resolve(
      await inputOrFlag(flags?.folderPath, FOLDER_FLAG_SPEC, {
        message: "Đường dẫn folder:",
        validate: (v) => {
          const trimmed = v.trim();
          if (trimmed.length === 0) return "Path bắt buộc";
          // Validate folder tồn tại + là thư mục (không phải file, không phải path sai).
          try {
            const st = statSync(resolve(trimmed));
            if (!st.isDirectory()) return `"${trimmed}" không phải thư mục (là file?).`;
            return true;
          } catch {
            return `Thư mục "${trimmed}" không tồn tại. Kiểm tra lại đường dẫn.`;
          }
        },
      }),
    );
    // P9: cảnh báo nếu folder có uncommitted changes (clone-from-remote sẽ KHÔNG có chúng).
    const dirty = await folderDirtyStatus(folderPath);
    if (dirty) {
      log.warn(`⚠ Folder có thay đổi chưa commit: ${dirty.slice(0, 120)}`);
      log.warn("  Clone lấy bản REMOTE → các thay đổi local này sẽ KHÔNG có trong src/<name>.");
      const proceed = await confirmOrFlag(
        flags ? flags.onDirtyFolder === "proceed" : undefined,
        DIRTY_FLAG_SPEC,
        {
          message: "Tiếp tục clone từ remote (bỏ thay đổi local chưa push)?",
          default: false,
        },
      );
      if (!proceed) {
        throw new Error(
          "Hủy. Commit + push thay đổi trong folder gốc rồi chạy lại 'avatar add repo'.",
        );
      }
    }
    const remote = await readFolderRemoteUrl(folderPath);
    if (remote) return { url: remote, mode: "clone" };

    // Folder không có remote → offer tạo remote GitHub + push. Folder ĐÃ có code →
    // clone về (mode clone) lấy bản remote vừa push.
    return { url: await ensureFolderHasRemote(folderPath, flags), mode: "clone" };
  }

  // source === "new": tạo repo GitHub rỗng → mode "new" (caller tạo local, KHÔNG clone).
  // Nhánh này chắc chắn cần GitHub → ensure gh cài + login TRƯỚC khi hỏi tên
  // (phát hiện sớm thiếu gh, không bắt user nhập xong mới fail).
  await ensureGitHubReady();
  const projectName = await inputOrFlag(flags?.newRepoName, NEW_NAME_FLAG_SPEC, {
    message: "Tên repo mới:",
    validate: validateGithubRepoName,
  });
  const visibility = await selectOrFlag<Visibility>(
    flags ? requireOneOf(flags.remoteVisibility, VISIBILITIES, "--remote-visibility") : undefined,
    VISIBILITY_FLAG_SPEC,
    {
      message: "Visibility?",
      choices: [
        { name: "private (mặc định)", value: "private" as const },
        { name: "public", value: "public" as const },
      ],
    },
  );

  // Tạo repo GitHub rỗng (recovery menu nếu fail) → trả URL + mode new.
  const url = await createEmptyRepoWithRecovery(projectName.trim(), visibility);
  return { url, mode: "new" };
}

// Folder chưa có remote → offer tạo repo GitHub + push code lên, trả URL remote.
// Thay vì bắt user tự `gh repo create` ngoài flow rồi quay lại.
async function ensureFolderHasRemote(folderPath: string, flags?: AddRepoFlags): Promise<string> {
  const { basename } = await import("node:path");
  log.warn(`Folder ${folderPath} chưa có remote origin.`);
  const create = await confirmOrFlag(
    flags
      ? requireOneOf(flags.createRemote, ["yes", "no"] as const, "--create-remote") === "yes"
      : undefined,
    CREATE_REMOTE_FLAG_SPEC,
    {
      message: "Tạo GitHub repo cho folder này + push code lên?",
      default: true,
    },
  );
  if (!create) {
    throw new Error(
      "Hủy. Cần remote để add repo. Tự tạo remote (gh repo create) rồi chạy lại, hoặc chọn nguồn khác.",
    );
  }
  // Tạo remote GitHub → cần gh cài + login. (Bug #68: không còn ensure vô điều
  // kiện ở đầu runAddRepo — chỉ nhánh thật sự cần GitHub mới gọi.)
  await ensureGitHubReady();

  // Đảm bảo folder là git repo + có ít nhất 1 commit (gh repo create --source --push cần).
  const { git } = await import("../lib/git-operations.js");
  const { pathExists } = await import("../lib/filesystem-helpers.js");
  const g = git(folderPath);
  if (!(await pathExists(`${folderPath}/.git`))) {
    log.info("Folder chưa git init — đang init...");
    await g.init();
  }
  // Stage + commit mọi thứ. Nếu nothing-to-commit (đã clean + đã có commit) → catch bỏ qua.
  log.info("Commit code hiện tại trong folder (baseline cho push)...");
  await g.add(".");
  await g.commit("chore: initial commit (avatar add repo)").catch(() => {
    // nothing to commit — folder đã clean + có commit từ trước.
  });

  const repoName = await inputOrFlag(flags?.newRepoName ?? flags?.name, NEW_NAME_FLAG_SPEC, {
    message: "Tên repo GitHub:",
    default: basename(folderPath),
    validate: validateGithubRepoName,
  });
  const visibility = await selectOrFlag<Visibility>(
    flags ? requireOneOf(flags.remoteVisibility, VISIBILITIES, "--remote-visibility") : undefined,
    VISIBILITY_FLAG_SPEC,
    {
      message: "Visibility?",
      choices: [
        { name: "private (mặc định)", value: "private" as const },
        { name: "public", value: "public" as const },
      ],
    },
  );

  const { createGithubRemoteFromFolder } = await import(
    "../lib/create-github-remote-from-folder.js"
  );
  // Recovery loop: tên trùng → đổi tên; sai account → switch + retry.
  const sshUrl = await createRemoteFromFolderWithRecovery(repoName.trim(), (name) => {
    return createGithubRemoteFromFolder({ folder: folderPath, name, visibility }).sshUrl;
  });
  log.success(`✓ Đã tạo remote + push: ${sshUrl}`);
  return sshUrl;
}

async function promptRepoName(url: string, nonInteractive = false): Promise<string> {
  const inferred = inferRepoNameFromUrl(url);
  // Non-interactive: tên suy từ URL là câu trả lời mặc định HIỂN NHIÊN (prompt
  // cũng chỉ đề nghị đúng giá trị này), nên dùng luôn thay vì bắt khai `--name`.
  return await inputOrFlag(nonInteractive ? inferred : undefined, NAME_FLAG_SPEC, {
    message: "Tên thư mục trong src/:",
    default: inferred,
  });
}

/** Nguồn repo suy từ cờ — dùng cho `selectOrFlag` ở `resolveRepoSource`. */
function resolveSourceFromFlags(flags: AddRepoFlags): "url" | "folder" | "new" | undefined {
  if (flags.repoSource === "url" || flags.repoSource === "folder" || flags.repoSource === "new") {
    return flags.repoSource;
  }
  return flags.url ? "url" : undefined;
}

// Side-effect chung cho CẢ nhánh clone + new sau khi repo về src/<name>:
// (1) ghi Avatar block vào <git-common-dir>/info/exclude (chống git add -A
//     nuốt .claude/AGENTS.md/CLAUDE.md/.gitnexus/ — ticket #138 phase-02: đích
//     ghi đổi từ .gitignore tracked sang info/exclude untracked), (2) index
//     GitNexus. Ghi exclude TRƯỚC index để .gitnexus/ (do index tạo) đã nằm
//     trong ignore.
export async function finalizeAddedRepo(
  workspaceRoot: string,
  repoPath: string,
  repoName: string,
): Promise<void> {
  // [N2] fail-soft: ghi info/exclude lỗi (fs permission/readonly, hoặc repo
  // không phải git repo) KHÔNG được giết add-loop ("Add repo khác nữa?") —
  // repo đã về src/ OK. Cảnh báo rõ. Nhất quán pattern fail-soft toàn codebase
  // (maybeIndexRepo, sync steps, doctor fixes).
  try {
    await writeAvatarBlockToGitExclude(repoPath, buildAvatarRepoGitignoreBlock(), {
      backupDir: join(workspaceRoot, ".claude", "_backup"),
    });
  } catch (err) {
    log.warn(
      `  ⚠ repo ${repoName} CHƯA có Avatar block trong <gitdir>/info/exclude (${err instanceof Error ? err.message : err}) — ĐỪNG 'git add -A' cho tới khi vá bằng 'avatar doctor --fix'.`,
    );
  }
  await maybeIndexRepo(workspaceRoot, repoPath);
  log.success(`✓ Done repo: ${repoName}`);
}

// Hỏi GitNexus index repo này (per-repo, P7). Sau index, nếu có key AI thì hỏi
// tạo wiki cho repo. Chỉ khi gitnexus đã cài.
//   workspaceRoot: đọc .claude/settings.json (credentials AI cho wiki)
//   repoPath: src/<name> — nơi analyze + chạy wiki
async function maybeIndexRepo(workspaceRoot: string, repoPath: string): Promise<void> {
  // GitNexus index = BẮT BUỘC khi add repo (không hỏi). GitNexus là hạ tầng cốt
  // lõi — đã cài mặc định lúc init. Nếu vẫn chưa có (cài fail trước đó) → skip im,
  // không chặn add-repo (fail-soft).
  const info = detectGitnexusInstallation();
  if (!info.installed) {
    log.dim("GitNexus chưa cài — skip index. Cài qua 'avatar gitnexus install'.");
    return;
  }

  try {
    // runGitnexusAnalyze tự xử repo TRỐNG: không tạo meta.json → throw
    // missing-output → catch dưới → skip im (không vỡ add-repo).
    await runGitnexusAnalyze(repoPath);
    log.success(`  ✓ GitNexus indexed src/${repoPath.split("/").pop()}`);
  } catch (err) {
    log.warn(
      `  ! GitNexus index fail (repo vẫn dùng được): ${err instanceof Error ? err.message : err}`,
    );
    return; // index fail / repo trống → không tạo wiki được (wiki cần index).
  }

  // Sau index OK: nếu có key AI (settings.json) → hỏi tạo wiki cho repo.
  // runGitnexusWikiConditional tự detect provider + key, tự confirm (cost prompt),
  // tự skip nếu subscription mode (không key). Wiki tạo trong <repo>/.gitnexus/wiki/.
  try {
    // settings ở workspaceRoot, wiki chạy trong repoPath (src/<name>).
    const wiki = await runGitnexusWikiConditional(workspaceRoot, repoPath);
    if (wiki.ran) {
      log.success(`  ✓ GitNexus wiki tạo cho src/${repoPath.split("/").pop()}`);
    } else if (wiki.reason === "subscription-mode") {
      log.dim("  (Subscription mode — không có API key cho wiki, skip.)");
    }
  } catch (err) {
    log.warn(`  ! Wiki fail (repo vẫn dùng được): ${err instanceof Error ? err.message : err}`);
  }
}
